import React, { useState, useEffect } from "react";
import { Typography, Input, Toastify, toast } from "top-brains-ui";
import styles from "@src/pages/login/set-password/SetPassword.module.scss";
import {
  COMMON_COLORS,
  ICON_COLOR_CONSTANTS,
} from "@src/styles/ColorConstants";
import { PASSWORD_VALIDATION } from "@src/constants/UiConstants";
import { useNavigate } from "react-router-dom";
import {
  useSetPasswordMutation,
  useValidateTokenMutation,
} from "@src/api/LoginApiSlice";
import { capitalizeFirstLetter } from "@src/utils/CommonUtils";
import GradiantButton from "@src/components/gradiant-btn/GradiantButton";
import { useDispatch } from "react-redux";
import { setUserLogin } from "@src/app/globalSlice";
import CommonLoader from "@src/components/loader/CommonLoader";
import { Icon } from "top-brains-ui";

const SetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | undefined>(
    undefined
  );
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | undefined
  >(undefined);
  const [passwordsMatchError, setPasswordsMatchError] = useState<
    string | undefined
  >(undefined);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const [hasLowercase, setHasLowercase] = useState(false);
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);
  const [isValidLength, setIsValidLength] = useState(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const token = window.location.pathname.split("/").pop();

  const [setPassword, { isLoading }] = useSetPasswordMutation();
  const [validateToken] = useValidateTokenMutation();

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordValidations, setShowPasswordValidations] = useState(false);
  const [touched, setTouched] = useState({
    newPassword: false,
    confirmPassword: false,
  });

  useEffect(() => {
    const checkTokenValidity = async () => {
      try {
        if (!token) {
          throw new Error("No token provided");
        }
        await validateToken({ token,isSetPassword:true }).unwrap();
      } catch (err) {
        console.error("Token validation failed:", err);
        navigate("/set-password-link-expired");
      }
    };

    checkTokenValidity();
  }, []);

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);

    setHasLowercase(/[a-z]/.test(value));
    setHasUppercase(/[A-Z]/.test(value));
    setHasNumber(/\d/.test(value));
    setHasSpecialChar(/[!@#$%^&*(),.?":{}|<>]/.test(value));
    setIsValidLength(value.length >= 8 && value.length <= 20);

    if (value.trim() === "" && touched.newPassword) {
      setPasswordError("New Password is required");
    } else if (
      !(
        /[a-z]/.test(value) &&
        /[A-Z]/.test(value) &&
        /\d/.test(value) &&
        /[!@#$%^&*(),.?":{}|<>]/.test(value) &&
        value.length >= 8
      )
    ) {
      setPasswordError("Password does not meet requirements");
    } else {
      setPasswordError(undefined);
    }

    if (confirmPassword && value !== confirmPassword) {
      setPasswordsMatchError("Passwords do not match");
    } else {
      setPasswordsMatchError(undefined);
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (value.trim() === "" && touched.confirmPassword) {
      setConfirmPasswordError("Confirm Password is required");
    } else {
      setConfirmPasswordError(undefined);
    }

    if (newPassword && value !== newPassword) {
      setPasswordsMatchError("Passwords do not match");
    } else {
      setPasswordsMatchError(undefined);
    }
  };

  const handleBlur = (field: "newPassword" | "confirmPassword") => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    // Trigger validation
    if (field === "newPassword" && newPassword.trim() === "") {
      setPasswordError("New Password is required");
    }
    if (field === "confirmPassword" && confirmPassword.trim() === "") {
      setConfirmPasswordError("Confirm Password is required");
    }
  };

  const handleSubmit = async () => {
    if (
      !newPassword ||
      !confirmPassword ||
      newPassword !== confirmPassword ||
      passwordsMatchError
    ) {
      return;
    }

    try {
      const response = await setPassword({
        token: token as string,
        password: newPassword,
      }).unwrap();
      toast.success("Success", capitalizeFirstLetter(response.response));
      navigate("/password-success", { state: { from: "set-password" } });
    } catch (err) {
      toast.error("Failed to set password");
      console.error("Failed to set password:", err);
      navigate("/set-password-link-expired");
    }
  };

  const handleSignInClick = () => {
    navigate("/");
    dispatch(setUserLogin(true));
  };

  return (
    <>
      <div className={styles.cardContainer}>
        <div className={styles.card}>
          <div className={styles.msgTitle}>
            <Typography
              fontWeight="semi-bold"
              fontSize={24}
              color="var(--modal-heading-color)"
            >
              Set Password
            </Typography>
          </div>
          <div className={styles.msgBody}>
            <div>
              <Typography
                fontSize={12}
                color="var(--modal-sub-heading-color)"
                fontWeight="medium"
              >
                Enter a new password to set the password on your account.
              </Typography>
            </div>
            <div className={styles.password}>
              <div className={styles.newPassword}>
                <div className={styles.passwordFieldWrapper}>
                  <Input
                    id="new-password-input"
                    type={showNewPassword ? "text" : "password"}
                    label="New Password"
                    name="newPassword"
                    value={newPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleNewPasswordChange(e);
                      if (e.target.value.length > 0) {
                        setShowPasswordValidations(true);
                      } else {
                        setShowPasswordValidations(false);
                      }
                    }}
                    onFocus={() => {
                      if (newPassword.length > 0) {
                        setShowPasswordValidations(true);
                      }
                      setIsPasswordFocused(true);
                    }}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      const isClickingEyeIcon = e.relatedTarget?.closest(
                        `.${styles.passwordIcon}`
                      );
                      if (!isClickingEyeIcon) {
                        setIsPasswordFocused(false);
                        setShowPasswordValidations(false);
                        handleBlur("newPassword");
                      }
                    }}
                    required
                    error={!!passwordError}
                    // helperText={passwordError}
                    variant="website"
                  />
                  <span
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowNewPassword((prev) => !prev);
                    }}
                    className={styles.passwordIcon}
                    tabIndex={-1}
                  >
                    {showNewPassword ? (
                      <Icon
                        name="view_access_icon"
                        color={ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON}
                      />
                    ) : (
                      <Icon
                        name="hide_access_icon"
                        color={ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON}
                      />
                    )}
                  </span>
                </div>
                <Typography className={styles.errorText} fontSize={12}>
                  {passwordError}
                </Typography>
                {isPasswordFocused && (
                  <div className={styles.passwordValidation}>
                    <div className={styles.validationColumn}>
                      <Typography
                        fontSize={12}
                        fontWeight="regular"
                        color={ICON_COLOR_CONSTANTS.TEXT_COLOR}
                        className={styles.validationMessage}
                      >
                        <span
                          className={`${styles.validationicon} ${
                            isValidLength ? styles.valid : styles.invalid
                          }`}
                        >
                          {isValidLength ? "✓" : "✗"}
                        </span>
                        {PASSWORD_VALIDATION.CHARACTER_LIMIT}
                      </Typography>
                      <Typography
                        fontSize={12}
                        fontWeight="regular"
                        color={ICON_COLOR_CONSTANTS.TEXT_COLOR}
                        className={styles.validationMessage}
                      >
                        <span
                          className={`${styles.validationicon} ${
                            hasUppercase ? styles.valid : styles.invalid
                          }`}
                        >
                          {hasUppercase ? "✓" : "✗"}
                        </span>
                        {PASSWORD_VALIDATION.UPPERCASE}
                      </Typography>
                      <Typography
                        fontSize={12}
                        fontWeight="regular"
                        color={ICON_COLOR_CONSTANTS.TEXT_COLOR}
                        className={styles.validationMessage}
                      >
                        <span
                          className={`${styles.validationicon} ${
                            hasSpecialChar ? styles.valid : styles.invalid
                          }`}
                        >
                          {hasSpecialChar ? "✓" : "✗"}
                        </span>
                        {PASSWORD_VALIDATION.SPECIAL_CHARACTER}
                      </Typography>
                    </div>
                    <div className={styles.validationColumn}>
                      <Typography
                        fontSize={12}
                        fontWeight="regular"
                        color={ICON_COLOR_CONSTANTS.TEXT_COLOR}
                        className={styles.validationMessage}
                      >
                        <span
                          className={`${styles.validationicon} ${
                            hasLowercase ? styles.valid : styles.invalid
                          }`}
                        >
                          {hasLowercase ? "✓" : "✗"}
                        </span>
                        {PASSWORD_VALIDATION.LOWER_CASE}
                      </Typography>
                      <Typography
                        fontSize={12}
                        fontWeight="regular"
                        color={ICON_COLOR_CONSTANTS.TEXT_COLOR}
                        className={styles.validationMessage}
                      >
                        <span
                          className={`${styles.validationicon} ${
                            hasNumber ? styles.valid : styles.invalid
                          }`}
                        >
                          {hasNumber ? "✓" : "✗"}
                        </span>
                        {PASSWORD_VALIDATION.NUMBER}
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.confirmPassword}>
                <div className={styles.passwordFieldWrapper}>
                  <Input
                    id="confirm-password-input"
                    type={showConfirmPassword ? "text" : "password"}
                    label="Confirm Password"
                    name="confirmPassword"
                    variant="website"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    required
                    onFocus={() => {
                      setShowPasswordValidations(false);
                      setIsPasswordFocused(false);
                    }}
                    error={!!confirmPasswordError || !!passwordsMatchError}
                    onBlur={() => handleBlur("confirmPassword")}
                    // helperText={confirmPasswordError ?? passwordsMatchError}
                  />
                  <span
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowConfirmPassword((prev) => !prev);
                    }}
                    className={styles.passwordIcon}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <Icon
                        name="view_access_icon"
                        color={ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON}
                      />
                    ) : (
                      <Icon
                        name="hide_access_icon"
                        color={ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON}
                      />
                    )}
                  </span>
                </div>
                <Typography className={styles.errorText} fontSize={12}>
                  {confirmPasswordError ?? passwordsMatchError}
                </Typography>
              </div>
            </div>
            <div className={styles.resendBtn}>
              <GradiantButton
                variant="secondary"
                labelClassName={styles.secondaryBackButton}
                onClick={handleSignInClick}
                label="Back to Sign In"
                className={styles.bottomButton}
                width="192px"
                height="44px"
                fontSize={16}
              />
              <GradiantButton
                variant="primary"
                type="submit"
                label="Set Password"
                width="192px"
                height="44px"
                fontSize={16}
                onClick={handleSubmit}
                className={styles.bottomButton}
                labelClassName={styles.backButton}
                disabled={
                  isLoading ||
                  !newPassword ||
                  !confirmPassword ||
                  newPassword !== confirmPassword ||
                  passwordError !== undefined ||
                  confirmPasswordError !== undefined ||
                  passwordsMatchError !== undefined
                }
              />
            </div>
            {isLoading && (
              <CommonLoader
                color={COMMON_COLORS.WEBSITE_GREEN_LIGHT}
                overLayColor={COMMON_COLORS.LOADER_OVERLAY}
              />
            )}
          </div>
        </div>
      </div>
      <Toastify />
    </>
  );
};

export default SetPassword;
